<script setup lang="ts">
const { isDark } = useAppearance()

const starfieldCanvas = ref<HTMLCanvasElement>()
const services = ['Laravel Development', 'CMS Migration', 'Managed Hosting', 'Support']
let stars: Array<{
  x: number
  y: number
  originalX?: number
  originalY?: number
  z: number
  size: number
  baseSize: number
  opacity: number
  twinkleSpeed: number
  twinklePhase: number
}> = []
let mouseX = 0
let mouseY = 0
let animationFrameId: number

const initStarfield = () => {
  if (!starfieldCanvas.value) return

  const canvas = starfieldCanvas.value
  const ctx = canvas.getContext('2d')
  if (!ctx) return

  const resizeCanvas = () => {
    canvas.width = window.innerWidth
    canvas.height = window.innerHeight // the hero only; the framed chapters below stay clear
    createStars()
  }

  const createStars = () => {
    stars = []
    const numStars = 150

    for (let i = 0; i < numStars; i++) {
      stars.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        z: Math.random() * 3 + 1,
        size: Math.random() * 2,
        baseSize: Math.random() * 1.2 + 0.3,
        opacity: Math.random() * 0.6 + 0.2,
        twinkleSpeed: Math.random() * 0.02 + 0.005,
        twinklePhase: Math.random() * Math.PI * 2,
      })
    }
  }

  const handleMouseMove = (e: MouseEvent) => {
    mouseX = e.clientX
    mouseY = e.clientY
  }

  const animate = () => {
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    stars.forEach((star) => {
      const dx = star.x - mouseX
      const dy = star.y - mouseY
      const distance = Math.sqrt(dx * dx + dy * dy)
      const maxDistance = 200

      let sizeMod = 1
      let opacityMod = 1

      if (distance < maxDistance) {
        const influence = 1 - distance / maxDistance
        sizeMod = 1 + influence * 3
        opacityMod = 1 + influence * 0.8

        // Push stars away from mouse
        const pushForce = influence * 8
        star.x += (dx / distance) * pushForce * 0.15
        star.y += (dy / distance) * pushForce * 0.15
      } else {
        // Slowly drift back
        const targetX = star.originalX || star.x
        const targetY = star.originalY || star.y
        star.x += (targetX - star.x) * 0.01
        star.y += (targetY - star.y) * 0.01
      }

      if (!star.originalX) {
        star.originalX = star.x
        star.originalY = star.y
      }

      // Twinkle
      star.twinklePhase += star.twinkleSpeed
      const twinkle = Math.sin(star.twinklePhase) * 0.4 + 0.6

      const finalOpacity = Math.min(star.opacity * opacityMod * twinkle, 1)
      const finalSize = star.baseSize * sizeMod

      // Create gradient with theme-aware colors
      const gradient = ctx.createRadialGradient(star.x, star.y, 0, star.x, star.y, finalSize * 3)
      if (isDark.value) {
        // Dark mode: blue/cyan stars
        gradient.addColorStop(0, `rgba(96, 165, 250, ${finalOpacity})`) // blu-400
        gradient.addColorStop(0.4, `rgba(255, 255, 255, ${finalOpacity * 0.9})`)
        gradient.addColorStop(1, `rgba(59, 130, 246, 0)`) // blu-500
      } else {
        // Light mode: darker blu particles
        gradient.addColorStop(0, `rgba(0, 170, 204, ${finalOpacity * 0.8})`) // blu-600
        gradient.addColorStop(0.4, `rgba(0, 128, 153, ${finalOpacity * 0.6})`) // blu-700
        gradient.addColorStop(1, `rgba(0, 85, 102, 0)`) // blu-800
      }

      ctx.fillStyle = gradient
      ctx.beginPath()
      ctx.arc(star.x, star.y, finalSize, 0, Math.PI * 2)
      ctx.fill()

      // Glow for bright stars
      if (finalOpacity > 0.5) {
        ctx.shadowBlur = isDark.value ? 15 : 10
        ctx.shadowColor = isDark.value ? 'rgba(96, 165, 250, 0.6)' : 'rgba(0, 170, 204, 0.5)'
        ctx.fill()
        ctx.shadowBlur = 0
      }
    })

    animationFrameId = requestAnimationFrame(animate)
  }

  resizeCanvas()
  window.addEventListener('resize', resizeCanvas)
  window.addEventListener('mousemove', handleMouseMove)
  animate()

  return () => {
    window.removeEventListener('resize', resizeCanvas)
    window.removeEventListener('mousemove', handleMouseMove)
    if (animationFrameId) {
      cancelAnimationFrame(animationFrameId)
    }
  }
}

let cleanup: (() => void) | undefined

onMounted(() => {
  // Defer the decorative starfield off the critical load path: it adds nothing
  // above the fold, so start it once the browser is idle (or after a short
  // fallback) rather than competing with hydration and the hero paint.
  const startStarfield = () => { cleanup = initStarfield() }
  if ('requestIdleCallback' in window) {
    requestIdleCallback(startStarfield, { timeout: 2000 })
  } else {
    setTimeout(startStarfield, 200)
  }
})

onUnmounted(() => {
  cleanup?.()
})
</script>

<template>
  <section class="relative overflow-x-clip">
    <!-- Server-rendered hero background — this <img> is the page's LCP element.
         IMPORTANT: height is 95vh, NOT 100vh/h-screen. Chromium's LCP algorithm
         excludes any image that covers the ENTIRE viewport (it treats full-viewport
         images as decorative backgrounds). At exactly 100vh this <img> stops being an
         LCP candidate, and since the hero has no other above-the-fold contentful
         element, Lighthouse reports NO_LCP. 95vh keeps it eligible. The bottom of the
         image is already faded out by the mask, so the 5vh reduction is invisible. -->
    <div class="absolute top-0 left-0 w-full h-[95vh] z-0 pointer-events-none overflow-hidden mask-b-from-60% mask-b-to-100% dark:opacity-50">
      <img
        src="/images/backgrounds/edinburgh_30.webp"
        alt=""
        fetchpriority="high"
        class="w-full h-full object-cover"
      />
    </div>

    <!-- Interactive starfield, kept to the hero and faded out at its foot so
         it never draws over the framed chapters below. -->
    <canvas
      ref="starfieldCanvas"
      class="absolute top-0 left-0 w-full pointer-events-none mask-b-from-70% mask-b-to-100%"
      :style="{ height: '100vh', opacity: 0.7, zIndex: 1 }"
    />

    <!-- Hero mark - exactly viewport height -->
    <div class="relative h-screen flex items-center justify-center overflow-hidden">
      <!-- The dot field the sections below stand on, behind the mark, with
           the light coming up once the mark has landed. -->
      <div class="pointer-events-none absolute inset-0 z-[2] [mask-image:radial-gradient(ellipse_45%_40%_at_50%_50%,#000_30%,transparent)]" aria-hidden="true">
        <div class="cell-dots absolute inset-0"></div>
      </div>
      <div class="hero-glow pointer-events-none absolute left-1/2 top-1/2 z-[2] h-96 w-[36rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-blu-500/10 blur-3xl" aria-hidden="true"></div>
      <div class="relative z-10 flex w-[86vw] max-w-[900px] flex-col items-center">
        <!-- A soft glow round the mark in dark, as on the Post Analogue hero. -->
        <HomeHeroWordmark class="w-full dark:drop-shadow-[0_0_18px_rgba(80,200,255,0.45)]" />
        <p class="hero-services mt-3 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-center font-semibold uppercase tracking-[0.14em] text-slate-700/80 dark:text-white/75 text-[11px] sm:text-sm md:text-base">
          <template v-for="(service, i) in services" :key="service">
            <span v-if="i" class="hero-sep h-1.5 w-1.5 shrink-0 rounded-full bg-blu-500" aria-hidden="true" />
            <span>{{ service }}</span>
          </template>
        </p>
      </div>

      <!-- Scroll indicator -->
      <div class="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2 animate-fade-in" style="animation-delay: 2s; opacity: 0; animation-fill-mode: forwards;">
        <div class="w-6 h-10 border-2 border-dark-500/30 dark:border-white/30 rounded-full flex justify-center pt-2">
          <div class="w-1 h-2 bg-dark-500/50 dark:bg-white/50 rounded-full animate-scroll-mouse"></div>
        </div>
        <span class="text-slate-700/75 dark:text-white/50 text-xs uppercase tracking-widest">Scroll</span>
      </div>
    </div>

  </section>
</template>

<style scoped>
/* The services row types on as the wordmark lands: a stepped reveal from the
   left, quicker than the Lottie's subtitle so it doesn't trail the mark. */
.hero-services {
  animation: hero-type 360ms steps(56, end) 600ms both;
}
@keyframes hero-type {
  from { clip-path: inset(0 100% 0 0); }
  to { clip-path: inset(0 0 0 0); }
}
.hero-glow {
  animation: hero-glow 1500ms ease-out 1200ms both;
}
@keyframes hero-glow {
  from { opacity: 0; }
  to { opacity: 1; }
}
@media (prefers-reduced-motion: reduce) {
  .hero-services,
  .hero-glow {
    animation: none;
  }
}
</style>
